import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { InfoMessage } from '@eika/attention-message';

import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { useKunde } from '../../kundeinfo/kunde-context';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { updateKontaktinfosvar } from '../reducer/sporsmalsflyt-actions';
import { IdentifikatorType } from '../reducer/sporsmalsflyt-types';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { KontaktinfoInput } from './kontaktinfo-field';
import { CurrentKontaktinfosporsmal } from './kontaktinfo-types';
import { getFeilmeldingFrom, isValid, Kontaktinfofelt } from './kontaktinfofelt';

import './kontaktinfo.scss';

const firstFieldWithError = (currentSporsmal: CurrentKontaktinfosporsmal): Kontaktinfofelt | undefined => {
    const fieldOrder = [
        Kontaktinfofelt.Navn,
        Kontaktinfofelt.Mobilnummer,
        Kontaktinfofelt.Epost,
        Kontaktinfofelt.Fodselsnummer,
        Kontaktinfofelt.Organisasjonsnummer,
        Kontaktinfofelt.Bankkontonummer,
    ];
    return fieldOrder.find((field) => !isValid(currentSporsmal, field));
};

const isFirstFieldWithError = (field: Kontaktinfofelt, currentSporsmal: CurrentKontaktinfosporsmal): boolean =>
    field === firstFieldWithError(currentSporsmal);

interface Props {
    currentSporsmal: CurrentKontaktinfosporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const Kontaktinfo = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string, felt: Kontaktinfofelt) => {
        dispatch(updateKontaktinfosvar(felt, value));
    };

    const fornavnRef = useFocusRef<HTMLInputElement>(focusOnMount);
    const kundeinfoExists = useKunde().kundeinfo !== undefined;
    const { fulltNavn, mobilnummer, epost, kontonummer, fodselsnummer, organisasjonsnummer } =
        currentSporsmal.sporsmal.kontaktInfosporsmalFieldLabels;

    return (
        <SporsmalHeader tittel={currentSporsmal.sporsmal.tekst}>
            <KontaktinfoInput
                name="navn"
                value={currentSporsmal.navn}
                label={fulltNavn}
                autocomplete="name"
                errorText={getFeilmeldingFrom(Kontaktinfofelt.Navn)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Navn)}
                onChange={(value) => {
                    onChange(value, Kontaktinfofelt.Navn);
                }}
                itemRef={mergeRefs([
                    fornavnRef,
                    isFirstFieldWithError(Kontaktinfofelt.Navn, currentSporsmal) ? errorRef : undefined,
                ])}
            />
            <KontaktinfoInput
                name="mobilnummer"
                value={currentSporsmal.mobilnummer}
                label={mobilnummer}
                type="tel"
                autocomplete="tel"
                errorText={getFeilmeldingFrom(Kontaktinfofelt.Mobilnummer)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Mobilnummer)}
                onChange={(value) => {
                    onChange(value, Kontaktinfofelt.Mobilnummer);
                }}
                itemRef={isFirstFieldWithError(Kontaktinfofelt.Mobilnummer, currentSporsmal) ? errorRef : undefined}
                small
            />
            <KontaktinfoInput
                name="epost"
                value={currentSporsmal.epost}
                label={epost}
                type="email"
                autocomplete="email"
                errorText={getFeilmeldingFrom(Kontaktinfofelt.Epost)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Epost)}
                onChange={(value) => {
                    onChange(value, Kontaktinfofelt.Epost);
                }}
                itemRef={isFirstFieldWithError(Kontaktinfofelt.Epost, currentSporsmal) ? errorRef : undefined}
            />
            {currentSporsmal.identifikatorType === IdentifikatorType.Fodselsnummer &&
                currentSporsmal.showFodselsnummer && (
                    <KontaktinfoInput
                        name="fnr"
                        value={currentSporsmal.fodselsnummer}
                        label={fodselsnummer}
                        numericKeyboard
                        errorText={getFeilmeldingFrom(Kontaktinfofelt.Fodselsnummer)}
                        isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Fodselsnummer)}
                        onChange={(value) => {
                            onChange(value, Kontaktinfofelt.Fodselsnummer);
                        }}
                        itemRef={
                            isFirstFieldWithError(Kontaktinfofelt.Fodselsnummer, currentSporsmal) ? errorRef : undefined
                        }
                    />
                )}
            {currentSporsmal.identifikatorType === IdentifikatorType.Organisasjonsnummer && (
                <KontaktinfoInput
                    name="orgnr"
                    value={currentSporsmal.organisasjonsnummer}
                    label={organisasjonsnummer}
                    numericKeyboard
                    errorText={getFeilmeldingFrom(Kontaktinfofelt.Organisasjonsnummer)}
                    isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Organisasjonsnummer)}
                    onChange={(value) => {
                        onChange(value, Kontaktinfofelt.Organisasjonsnummer);
                    }}
                    itemRef={
                        isFirstFieldWithError(Kontaktinfofelt.Organisasjonsnummer, currentSporsmal)
                            ? errorRef
                            : undefined
                    }
                />
            )}
            {(currentSporsmal.sporsmal.askForBankkontonummer || !kundeinfoExists) && (
                <>
                    <KontaktinfoInput
                        name="bankkontonummer"
                        value={currentSporsmal.bankkontonummer}
                        label={kontonummer}
                        onChange={(value) => {
                            onChange(value, Kontaktinfofelt.Bankkontonummer);
                        }}
                        numericKeyboard
                        errorText={getFeilmeldingFrom(Kontaktinfofelt.Bankkontonummer)}
                        isValid={!showFeilmeldinger || isValid(currentSporsmal, Kontaktinfofelt.Bankkontonummer)}
                    />
                    <InfoMessage text="Hvis skaden dekkes av forsikringen din, betaler vi erstatningen til ditt kontonummer." />
                </>
            )}
        </SporsmalHeader>
    );
};
